import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Save, Truck, Plus, Trash2, X } from "lucide-react";

export default function VehicleSettingsDialog({ open, onOpenChange, onSaved, vehicles, api }) {
  const [formData, setFormData] = useState({});
  const [newVehicleName, setNewVehicleName] = useState("");
  const [newVehicleBreakeven, setNewVehicleBreakeven] = useState("");
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    // Initialize form data with existing vehicles
    const initialData = {};
    vehicles.forEach((v) => {
      initialData[v.vehicle_name] = v.daily_breakeven?.toString() || "";
    });
    setFormData(initialData);
  }, [vehicles]);

  const handleChange = (vehicleName, value) => {
    setFormData((prev) => ({
      ...prev,
      [vehicleName]: value,
    }));
  };

  const handleAddVehicle = async () => {
    if (!newVehicleName.trim()) {
      toast.error("Please enter a vehicle name");
      return;
    }

    try {
      await axios.post(`${api}/vehicles`, {
        vehicle_name: newVehicleName.trim(),
        daily_breakeven: parseFloat(newVehicleBreakeven) || 0,
      });
      
      setNewVehicleName("");
      setNewVehicleBreakeven("");
      setShowAddForm(false);
      onSaved();
      toast.success(`Vehicle "${newVehicleName}" added`);
    } catch (error) {
      console.error("Error adding vehicle:", error);
      toast.error("Failed to add vehicle");
    }
  };

  const handleRemoveVehicle = async (vehicleName) => {
    if (!window.confirm(`Remove "${vehicleName}" from the fleet?`)) {
      return;
    }

    try {
      await axios.delete(`${api}/vehicles/${encodeURIComponent(vehicleName)}`);
      onSaved();
      toast.success(`Vehicle "${vehicleName}" removed`);
    } catch (error) {
      console.error("Error removing vehicle:", error);
      toast.error("Failed to remove vehicle");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save all settings
      const promises = Object.entries(formData).map(([vehicleName, breakeven]) => {
        const value = parseFloat(breakeven) || 0;
        return axios.post(`${api}/vehicle-settings`, {
          vehicle_name: vehicleName,
          daily_breakeven: value,
        });
      });
      
      await Promise.all(promises);
      onSaved();
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-[#E2E8F0] max-w-md max-h-[90vh] overflow-y-auto" data-testid="settings-form">
        <DialogHeader>
          <DialogTitle className="text-[#0F172A] text-xl flex items-center gap-2">
            <Truck className="w-5 h-5 text-[#0891B2]" />
            Vehicle Settings
          </DialogTitle>
          <DialogDescription className="text-[#64748B]">
            Manage vehicles and set daily breakeven targets
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Existing Vehicles */}
          {vehicles.length === 0 ? (
            <p className="text-[#94A3B8] text-sm text-center py-4">No vehicles added yet</p>
          ) : (
            vehicles.map((vehicle) => (
              <div key={vehicle.vehicle_name} className="flex items-center gap-3 group">
                <Label className="text-[#0F172A] w-28 flex-shrink-0">{vehicle.vehicle_name}</Label>
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]">R</span>
                  <Input
                    type="number"
                    value={formData[vehicle.vehicle_name] || ""}
                    onChange={(e) => handleChange(vehicle.vehicle_name, e.target.value)}
                    placeholder="0"
                    className="pl-7 bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A] placeholder:text-[#94A3B8]"
                    data-testid={`setting-${vehicle.vehicle_name.toLowerCase()}`}
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveVehicle(vehicle.vehicle_name)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-[#DC2626] hover:text-[#DC2626] hover:bg-[#DC2626]/10"
                  data-testid={`remove-${vehicle.vehicle_name.toLowerCase()}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))
          )}

          {/* Add New Vehicle Form */}
          {showAddForm ? (
            <div className="border border-dashed border-[#0891B2]/50 rounded-lg p-4 space-y-3 bg-[#0891B2]/5">
              <div className="flex items-center justify-between">
                <Label className="text-[#0891B2] text-sm font-medium">Add New Vehicle</Label>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowAddForm(false)}
                  className="h-6 w-6 text-[#64748B] hover:text-[#0F172A]"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <Input
                type="text"
                value={newVehicleName}
                onChange={(e) => setNewVehicleName(e.target.value)}
                placeholder="Vehicle name"
                className="bg-white border-[#E2E8F0] text-[#0F172A] placeholder:text-[#94A3B8]"
                data-testid="new-vehicle-name"
              />
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]">R</span>
                <Input
                  type="number"
                  value={newVehicleBreakeven}
                  onChange={(e) => setNewVehicleBreakeven(e.target.value)}
                  placeholder="Daily breakeven"
                  className="pl-7 bg-white border-[#E2E8F0] text-[#0F172A] placeholder:text-[#94A3B8]"
                  data-testid="new-vehicle-breakeven"
                />
              </div>
              <Button
                onClick={handleAddVehicle}
                className="w-full bg-[#0891B2] hover:bg-[#0E7490] text-white"
                data-testid="confirm-add-vehicle"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Vehicle
              </Button>
            </div>
          ) : (
            <Button
              variant="outline"
              onClick={() => setShowAddForm(true)}
              className="w-full border-dashed border-[#0891B2]/50 text-[#0891B2] hover:bg-[#0891B2]/10 hover:text-[#0891B2]"
              data-testid="add-vehicle-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add New Vehicle
            </Button>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1 bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A] hover:bg-[#F1F5F9] hover:text-[#0F172A]"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || vehicles.length === 0}
            className="flex-1 bg-[#0891B2] hover:bg-[#0E7490] text-white"
            data-testid="save-settings-btn"
          >
            <Save className="mr-2 h-4 w-4" />
            {saving ? "Saving..." : "Save All"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
